import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  UserPlus, Users, Link2, Trash2, Pencil, CheckCircle2, Circle, Mail, Copy, FileCheck2,
} from 'lucide-react';
import { toast } from 'sonner';

const RELATII = ['soț/soție', 'părinte', 'copil', 'frate/soră', 'rudă', 'partener', 'altcineva'];

const EMPTY = {
  nume: '', prenume: '', relatie: '', telefon: '', email: '', cnp: '',
  venitLunar: '', angajator: '', rateLunare: '', notite: '',
};

const lei = (v) => (v || v === 0 ? `${Number(v).toLocaleString('ro-RO')} lei` : '—');

export default function CoDebitoriPanel({ api, clientId, solicitareId, coDebitori, atasati, onChanged }) {
  const [form, setForm] = useState(null); // null = închis, {} = adăugare, {...} = editare
  const [busy, setBusy] = useState(false);
  const [linkFor, setLinkFor] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');

  const lista = coDebitori || [];
  const atasate = atasati || [];
  const activi = lista.filter((x) => atasate.includes(x.id));
  const venitTotal = activi.reduce((s, x) => s + (Number(x.venitLunar) || 0), 0);
  const rateTotal = activi.reduce((s, x) => s + (Number(x.rateLunare) || 0), 0);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.nume?.trim()) return toast.error('Numele este obligatoriu');
    setBusy(true);
    try {
      const body = {
        ...form,
        venitLunar: form.venitLunar === '' ? null : Number(form.venitLunar),
        rateLunare: form.rateLunare === '' ? null : Number(form.rateLunare),
        solicitareId,
      };
      if (form.id) {
        await api.put(`/clients/${clientId}/codebitori/${form.id}`, body);
        toast.success('Codebitor actualizat');
      } else {
        await api.post(`/clients/${clientId}/codebitori`, body);
        toast.success('Codebitor adăugat la client și la creditul curent');
      }
      setForm(null);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (cb) => {
    const on = atasate.includes(cb.id);
    try {
      if (on) await api.del(`/solicitari/${solicitareId}/codebitori/${cb.id}`);
      else await api.post(`/solicitari/${solicitareId}/codebitori/${cb.id}`);
      toast.success(on ? 'Scos din creditul curent' : 'Adăugat la creditul curent');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare');
    }
  };

  const remove = async (cb) => {
    if (!window.confirm(`Ștergi definitiv ${cb.nume} ${cb.prenume || ''} din fișa clientului?`)) return;
    try {
      await api.del(`/clients/${clientId}/codebitori/${cb.id}`);
      toast.success('Codebitor șters');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare');
    }
  };

  const genLink = async (cb, trimiteEmail) => {
    setBusy(true);
    try {
      const r = await api.post(`/clients/${clientId}/codebitori/${cb.id}/link`, {
        solicitareId, trimiteEmail,
      });
      setLinkUrl(r.url);
      setLinkFor(cb);
      toast.success(r.emailSent ? `Link trimis pe ${cb.email}` : 'Link generat');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la generarea linkului');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="crm-codebitori-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" /> Codebitori ({activi.length} la creditul curent)
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => setForm({ ...EMPTY })}
            data-testid="crm-codebitor-add">
            <UserPlus className="h-4 w-4 mr-1" /> Adaugă codebitor
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {lista.length === 0 && (
          <p className="text-sm text-gray-500" data-testid="crm-codebitori-empty">
            Niciun codebitor. Persoanele adăugate rămân pe client și le poți lega la oricare credit viitor.
          </p>
        )}

        {lista.map((cb) => {
          const on = atasate.includes(cb.id);
          return (
            <div key={cb.id}
              className={`rounded-lg border p-3 ${on ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200'}`}
              data-testid={`crm-codebitor-${cb.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => toggle(cb)} title={on ? 'Scoate din creditul curent' : 'Adaugă la creditul curent'}
                      className="text-blue-600" data-testid={`crm-codebitor-toggle-${cb.id}`}>
                      {on ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4 text-gray-300" />}
                    </button>
                    <span className="font-medium text-sm">{cb.nume} {cb.prenume}</span>
                    {cb.relatie && <Badge variant="outline" className="text-[10px]">{cb.relatie}</Badge>}
                    {cb.sursa === 'formular' && (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">din formular</Badge>
                    )}
                    {cb.formularCompletat && (
                      <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">
                        <FileCheck2 className="h-3 w-3 mr-1" /> formular completat
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                    <span>{cb.telefon || '—'}</span>
                    <span>{cb.email || '—'}</span>
                    {cb.angajator && <span>{cb.angajator}</span>}
                    <span>Venit: <strong>{lei(cb.venitLunar)}</strong></span>
                    {cb.rateLunare ? <span>Rate: {lei(cb.rateLunare)}</span> : null}
                  </div>
                  {cb.notite && <p className="text-xs text-gray-500 mt-1">{cb.notite}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setForm({ ...EMPTY, ...cb })}
                    title="Editează" data-testid={`crm-codebitor-edit-${cb.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => genLink(cb, false)}
                    title="Generează link formular" data-testid={`crm-codebitor-link-${cb.id}`}>
                    <Link2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy || !cb.email}
                    onClick={() => genLink(cb, true)}
                    title={cb.email ? `Trimite linkul pe ${cb.email}` : 'Fără email salvat'}
                    data-testid={`crm-codebitor-email-${cb.id}`}>
                    <Mail className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(cb)}
                    title="Șterge" data-testid={`crm-codebitor-delete-${cb.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}

        {activi.length > 0 && (
          <div className="text-xs text-gray-700 pt-1 border-t" data-testid="crm-codebitori-total">
            Venit codebitori la creditul curent: <strong>{lei(venitTotal)}</strong>
            {rateTotal > 0 && <> · rate existente: <strong>{lei(rateTotal)}</strong></>}
          </div>
        )}
      </CardContent>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-w-xl" data-testid="crm-codebitor-dialog">
          <DialogHeader>
            <DialogTitle>{form?.id ? 'Editează codebitorul' : 'Codebitor nou'}</DialogTitle>
            <DialogDescription>
              Persoana se salvează pe client{solicitareId ? ' și se leagă automat de creditul deschis' : ''}.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nume *</Label>
                <Input value={form.nume} onChange={(e) => set('nume', e.target.value)}
                  data-testid="crm-codebitor-nume" />
              </div>
              <div>
                <Label className="text-xs">Prenume</Label>
                <Input value={form.prenume} onChange={(e) => set('prenume', e.target.value)}
                  data-testid="crm-codebitor-prenume" />
              </div>
              <div>
                <Label className="text-xs">Relația cu clientul</Label>
                <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.relatie}
                  onChange={(e) => set('relatie', e.target.value)} data-testid="crm-codebitor-relatie">
                  <option value="">—</option>
                  {RELATII.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">CNP</Label>
                <Input value={form.cnp} onChange={(e) => set('cnp', e.target.value)}
                  data-testid="crm-codebitor-cnp" />
              </div>
              <div>
                <Label className="text-xs">Telefon</Label>
                <Input value={form.telefon} onChange={(e) => set('telefon', e.target.value)}
                  data-testid="crm-codebitor-telefon" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={form.email} onChange={(e) => set('email', e.target.value)}
                  data-testid="crm-codebitor-email" />
              </div>
              <div>
                <Label className="text-xs">Venit lunar net (lei)</Label>
                <Input type="number" value={form.venitLunar}
                  onChange={(e) => set('venitLunar', e.target.value)} data-testid="crm-codebitor-venit" />
              </div>
              <div>
                <Label className="text-xs">Rate lunare existente (lei)</Label>
                <Input type="number" value={form.rateLunare}
                  onChange={(e) => set('rateLunare', e.target.value)} data-testid="crm-codebitor-rate" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Angajator</Label>
                <Input value={form.angajator} onChange={(e) => set('angajator', e.target.value)}
                  data-testid="crm-codebitor-angajator" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notițe</Label>
                <Input value={form.notite} onChange={(e) => set('notite', e.target.value)}
                  data-testid="crm-codebitor-notite" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Anulează</Button>
            <Button onClick={save} disabled={busy} data-testid="crm-codebitor-save">
              {busy ? 'Se salvează…' : 'Salvează'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkFor} onOpenChange={(v) => !v && setLinkFor(null)}>
        <DialogContent data-testid="crm-codebitor-link-dialog">
          <DialogHeader>
            <DialogTitle>Link formular codebitor</DialogTitle>
            <DialogDescription>
              {linkFor?.nume} {linkFor?.prenume} completează formularul propriu; datele intră automat în fișă.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={linkUrl} data-testid="crm-codebitor-link-value" />
            <Button variant="outline" onClick={() => {
              navigator.clipboard?.writeText(linkUrl);
              toast.success('Link copiat');
            }} data-testid="crm-codebitor-link-copy">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkFor(null)}>Închide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
